import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Sidebar } from '../components/Sidebar';
import { PageHeader, LoadingSpinner } from '../components/UIComponents';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { clientsAPI, telemetryTestAPI, invoicingAPI } from '../utils/api';
import {
  Building2, FileText, Upload, Trash2, Download, ChevronRight,
  Zap, Battery, MapPin, Activity, WifiOff, Wifi, AlertCircle,
  FileSpreadsheet, Users, Package, ArrowLeft, Plus,
  Sparkles, RefreshCw, Copy, ChevronDown,
} from 'lucide-react';
import { toast } from 'sonner';
import { CreateSiteModal } from '../components/CreateSiteModal';
import { CreateAssetModal } from '../components/CreateAssetModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DOC_TYPE_LABELS = {
  proposal: 'Proposal',
  agreement: 'Agreement',
  invoice: 'Invoice',
  purchase_order: 'Purchase Order',
  nda: 'NDA',
  other: 'Other',
};

const DOC_TYPE_COLORS = {
  proposal: 'bg-blue-50 text-blue-700 border-blue-200',
  agreement: 'bg-green-50 text-green-700 border-green-200',
  invoice: 'bg-amber-50 text-amber-700 border-amber-200',
  purchase_order: 'bg-purple-50 text-purple-700 border-purple-200',
  nda: 'bg-rose-50 text-rose-700 border-rose-200',
  other: 'bg-slate-50 text-slate-700 border-slate-200',
};

export default function ClientDetailPage() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showUpload, setShowUpload] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadDocType, setUploadDocType] = useState('other');
  const [uploadDescription, setUploadDescription] = useState('');
  const [uploading, setUploading] = useState(false);
  const [testingAsset, setTestingAsset] = useState(null);
  const [telemetryResult, setTelemetryResult] = useState(null);
  const [showCreateSite, setShowCreateSite] = useState(false);
  const [showCreateAsset, setShowCreateAsset] = useState(false);
  const [createAssetSiteId, setCreateAssetSiteId] = useState(null);

  const fetchClientDetail = useCallback(async () => {
    try {
      const res = await clientsAPI.getDetail(clientId);
      setData(res.data);
    } catch (err) {
      toast.error('Failed to load client details');
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    fetchClientDetail();
  }, [fetchClientDetail]);

  const handleUploadDocument = async (e) => {
    e.preventDefault();
    if (!uploadFile) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('doc_type', uploadDocType);
      formData.append('description', uploadDescription);
      await clientsAPI.uploadDocument(clientId, formData);
      toast.success('Document uploaded');
      setShowUpload(false);
      setUploadFile(null);
      setUploadDescription('');
      fetchClientDetail();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      await clientsAPI.deleteDocument(clientId, docId);
      toast.success('Document deleted');
      fetchClientDetail();
    } catch (err) {
      toast.error('Failed to delete document');
    }
  };

  const handleDownloadDocument = async (docId) => {
    try {
      const res = await clientsAPI.downloadDocument(clientId, docId);
      if (res.data?.download_url) {
        window.open(res.data.download_url, '_blank');
      }
    } catch (err) {
      toast.error('Download not available');
    }
  };

  const handleTestTelemetry = async (assetId) => {
    setTestingAsset(assetId);
    setTelemetryResult(null);
    try {
      const res = await telemetryTestAPI.testLink(assetId);
      setTelemetryResult(res.data);
    } catch (err) {
      setTelemetryResult({ status: 'ERROR', message: err.response?.data?.detail || 'Test failed' });
    }
  };

  if (loading) {
    return (
      <div className="flex">
        <Sidebar />
        <div className="flex-1 ml-0 lg:ml-64 pt-14 lg:pt-0"><LoadingSpinner /></div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex">
        <Sidebar />
        <div className="flex-1 ml-0 lg:ml-64 pt-14 lg:pt-0 p-8">
          <p className="text-slate-500">Client not found.</p>
        </div>
      </div>
    );
  }

  const { summary, sites = [], documents = [], contracts = [] } = data;
  const tabs = [
    { key: 'overview', label: 'Overview' },
    { key: 'sites', label: `Sites (${sites.length})` },
    { key: 'documents', label: `Documents (${documents.length})` },
    { key: 'billing', label: 'Billing & Payments' },
    { key: 'agreement', label: 'Agreement Extract' },
  ];

  return (
    <div className="flex min-h-screen bg-slate-50" data-testid="client-detail-page">
      <Sidebar />
      <div className="flex-1 ml-0 lg:ml-64 pt-14 lg:pt-0">
        <div className="p-8">
          {/* Back button & header */}
          <button
            onClick={() => navigate('/clients')}
            className="flex items-center text-sm text-slate-500 hover:text-slate-800 mb-4 transition-colors"
            data-testid="back-to-clients"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Back to Clients
          </button>

          <div className="flex items-start justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-slate-900" data-testid="client-name">{data.name}</h1>
              <p className="text-sm text-slate-500 mt-1">{data.contact_person} &middot; {data.contact_email}</p>
            </div>
            <Button
              variant="outline" size="sm"
              onClick={() => navigate(`/clients/${clientId}/billing`)}
              data-testid="go-to-billing"
            >
              <FileSpreadsheet className="w-4 h-4 mr-2" /> Full Billing
            </Button>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <SummaryCard icon={Building2} label="Sites" value={summary?.total_sites || 0} color="blue" />
            <SummaryCard icon={Package} label="Assets" value={summary?.total_assets || 0} color="emerald" />
            <SummaryCard icon={Zap} label="Power (kW)" value={summary?.total_power_kw?.toFixed(0) || 0} color="amber" />
            <SummaryCard icon={Battery} label="Capacity (kWh)" value={summary?.total_capacity_kwh?.toFixed(0) || 0} color="purple" />
          </div>

          {/* Tabs */}
          <div className="flex border-b border-slate-200 mb-6">
            {tabs.map(t => (
              <button
                key={t.key}
                onClick={() => setActiveTab(t.key)}
                data-testid={`tab-${t.key}`}
                className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === t.key
                    ? 'border-slate-900 text-slate-900'
                    : 'border-transparent text-slate-500 hover:text-slate-700'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          {activeTab === 'overview' && (
            <OverviewTab data={data} summary={summary} contracts={contracts} navigate={navigate} />
          )}
          {activeTab === 'sites' && (
            <SitesTab
              sites={sites}
              navigate={navigate}
              onTestTelemetry={handleTestTelemetry}
              testingAsset={testingAsset}
              telemetryResult={telemetryResult}
              onCreateSite={() => setShowCreateSite(true)}
              onCreateAsset={(siteId) => { setCreateAssetSiteId(siteId); setShowCreateAsset(true); }}
            />
          )}
          {activeTab === 'documents' && (
            <DocumentsTab
              documents={documents}
              onUpload={() => setShowUpload(true)}
              onDelete={handleDeleteDocument}
              onDownload={handleDownloadDocument}
            />
          )}
          {activeTab === 'billing' && (
            <BillingTab summary={summary} clientId={clientId} navigate={navigate} />
          )}
          {activeTab === 'agreement' && (
            <AgreementExtractTab clientId={clientId} documents={documents} onSwitchToBilling={() => setActiveTab('billing')} />
          )}
        </div>
      </div>

      {/* Upload Dialog */}
      <Dialog open={showUpload} onOpenChange={setShowUpload}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Upload Document</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUploadDocument} className="space-y-4 mt-4">
            <div>
              <Label>Document Type</Label>
              <select
                value={uploadDocType}
                onChange={e => setUploadDocType(e.target.value)}
                className="w-full mt-1.5 px-3 py-2 border border-slate-200 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
                data-testid="doc-type-select"
              >
                {Object.entries(DOC_TYPE_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </div>
            <div>
              <Label>Description</Label>
              <Input
                value={uploadDescription}
                onChange={e => setUploadDescription(e.target.value)}
                placeholder="Brief description..."
                className="mt-1.5"
                data-testid="doc-description-input"
              />
            </div>
            <div>
              <Label>File</Label>
              <input
                type="file"
                onChange={e => setUploadFile(e.target.files?.[0] || null)}
                className="mt-1.5 block w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200"
                data-testid="doc-file-input"
                required
              />
            </div>
            <div className="flex justify-end space-x-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowUpload(false)}>Cancel</Button>
              <Button type="submit" disabled={uploading} className="bg-slate-900 hover:bg-slate-800" data-testid="doc-upload-submit">
                {uploading ? 'Uploading...' : 'Upload'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <CreateSiteModal
        open={showCreateSite}
        onClose={() => setShowCreateSite(false)}
        onCreated={() => fetchClientDetail()}
        defaultClientId={clientId}
      />

      <CreateAssetModal
        open={showCreateAsset}
        onClose={() => setShowCreateAsset(false)}
        onCreated={() => fetchClientDetail()}
        siteId={createAssetSiteId}
      />
    </div>
  );
}

function SummaryCard({ icon: Icon, label, value, color }) {
  const colors = {
    blue: 'bg-blue-50 text-blue-600',
    emerald: 'bg-emerald-50 text-emerald-600',
    amber: 'bg-amber-50 text-amber-600',
    purple: 'bg-purple-50 text-purple-600',
  };
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-4" data-testid={`summary-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-center space-x-3">
        <div className={`w-10 h-10 rounded-sm flex items-center justify-center ${colors[color]}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <p className="text-2xl font-bold text-slate-900">{value}</p>
          <p className="text-xs text-slate-500">{label}</p>
        </div>
      </div>
    </div>
  );
}

function OverviewTab({ data, summary, contracts, navigate }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Contact Info */}
      <div className="bg-white border border-slate-200 rounded-sm p-6">
        <h3 className="font-semibold text-slate-900 mb-4">Contact Information</h3>
        <div className="space-y-3 text-sm">
          <div className="flex items-center"><Users className="w-4 h-4 mr-3 text-slate-400" /><span>{data.contact_person}</span></div>
          <div className="flex items-center"><FileText className="w-4 h-4 mr-3 text-slate-400" /><span>{data.contact_email}</span></div>
          <div className="flex items-center"><Zap className="w-4 h-4 mr-3 text-slate-400" /><span>{data.contact_phone}</span></div>
          <div className="flex items-start"><MapPin className="w-4 h-4 mr-3 text-slate-400 mt-0.5" /><span>{data.address}</span></div>
        </div>
      </div>

      {/* Billing Summary */}
      <div className="bg-white border border-slate-200 rounded-sm p-6">
        <h3 className="font-semibold text-slate-900 mb-4">Billing Summary</h3>
        <div className="space-y-3 text-sm">
          <div className="flex justify-between"><span className="text-slate-500">Total Billed</span><span className="font-semibold">INR {(summary?.total_billed_amount || 0).toLocaleString()}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">Pending Cycles</span><span className="font-semibold text-amber-600">{summary?.pending_billing_cycles || 0}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">Approved Cycles</span><span className="font-semibold text-green-600">{summary?.approved_billing_cycles || 0}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">Locked Cycles</span><span className="font-semibold">{summary?.locked_billing_cycles || 0}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">Active Contracts</span><span className="font-semibold">{summary?.active_contracts || 0}</span></div>
        </div>
      </div>

      {/* Contracts */}
      {contracts.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-sm p-6 lg:col-span-2">
          <h3 className="font-semibold text-slate-900 mb-4">Active Contracts</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-slate-500">
                  <th className="pb-2 font-medium">Contract</th>
                  <th className="pb-2 font-medium">Type</th>
                  <th className="pb-2 font-medium">Rate</th>
                  <th className="pb-2 font-medium">Period</th>
                </tr>
              </thead>
              <tbody>
                {contracts.map(c => (
                  <tr key={c.contract_id} className="border-b border-slate-100">
                    <td className="py-2.5 font-medium text-slate-900">{c.contract_name}</td>
                    <td className="py-2.5 capitalize">{c.billing_type?.replace('_', ' ')}</td>
                    <td className="py-2.5">INR {c.rate_per_kwh}/kWh</td>
                    <td className="py-2.5 text-slate-500">{c.start_date} - {c.end_date}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function SitesTab({ sites, navigate, onTestTelemetry, testingAsset, telemetryResult, onCreateSite, onCreateAsset }) {
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="font-semibold text-slate-900">Sites ({sites.length})</h3>
        <Button onClick={onCreateSite} className="bg-slate-900 hover:bg-slate-800" data-testid="create-site-from-client-detail">
          <Plus className="w-4 h-4 mr-2" /> Create Site
        </Button>
      </div>
      {sites.length === 0 && (
        <div className="text-center py-12 bg-white border border-slate-200 rounded-sm">
          <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 text-sm mb-3">No sites found for this client.</p>
          <Button onClick={onCreateSite} className="bg-slate-900 hover:bg-slate-800" data-testid="create-site-empty-cta">
            <Plus className="w-4 h-4 mr-2" /> Create Site
          </Button>
        </div>
      )}
      {sites.map(site => (
        <div key={site.site_id} className="bg-white border border-slate-200 rounded-sm p-5" data-testid={`site-card-${site.site_id}`}>
          <div className="flex items-center justify-between mb-3">
            <div
              className="flex items-center space-x-3 cursor-pointer hover:text-blue-700 transition-colors"
              onClick={() => navigate(`/sites/${site.site_id}`)}
              data-testid={`site-link-${site.site_id}`}
            >
              <Building2 className="w-5 h-5 text-blue-600" />
              <h4 className="font-semibold text-slate-900">{site.name}</h4>
              <ChevronRight className="w-4 h-4 text-slate-400" />
            </div>
            <span className={`px-2 py-0.5 text-xs rounded-full ${
              site.live_status === 'online' ? 'bg-green-100 text-green-700' :
              site.live_status === 'partial_offline' ? 'bg-amber-100 text-amber-700' :
              'bg-red-100 text-red-700'
            }`}>
              {site.live_status || 'offline'}
            </span>
          </div>
          <p className="text-sm text-slate-500 mb-3">{site.location}</p>

          {/* Assets Table */}
          {site.assets && site.assets.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm border border-slate-100 rounded">
                <thead>
                  <tr className="bg-slate-50 text-left text-slate-500 text-xs uppercase tracking-wider">
                    <th className="px-3 py-2 font-medium">Asset</th>
                    <th className="px-3 py-2 font-medium">Vendor / Model</th>
                    <th className="px-3 py-2 font-medium">PCS (kW)</th>
                    <th className="px-3 py-2 font-medium">Battery (kWh)</th>
                    <th className="px-3 py-2 font-medium">Telemetry</th>
                    <th className="px-3 py-2 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {site.assets.map(asset => {
                    const hasTelemetry = !!asset.last_telemetry_at;
                    const hasVendorId = !!asset.vendor_asset_id;
                    return (
                      <tr key={asset.asset_id} className="border-t border-slate-100" data-testid={`asset-row-${asset.asset_id}`}>
                        <td className="px-3 py-2.5">
                          <span
                            className="font-medium text-slate-900 cursor-pointer hover:text-blue-700 transition-colors"
                            onClick={() => navigate(`/assets/${asset.asset_id}`)}
                          >
                            {asset.asset_name}
                          </span>
                        </td>
                        <td className="px-3 py-2.5 text-slate-600">
                          {asset.manufacturer || '-'}{asset.model ? ` / ${asset.model}` : ''}
                        </td>
                        <td className="px-3 py-2.5">{asset.installed_power_kw}</td>
                        <td className="px-3 py-2.5">{asset.installed_capacity_kwh}</td>
                        <td className="px-3 py-2.5">
                          {hasTelemetry ? (
                            <span className="inline-flex items-center text-green-600 text-xs"><Wifi className="w-3 h-3 mr-1" />Active</span>
                          ) : hasVendorId ? (
                            <span className="inline-flex items-center text-amber-600 text-xs"><AlertCircle className="w-3 h-3 mr-1" />No Data</span>
                          ) : (
                            <span className="inline-flex items-center text-slate-400 text-xs"><WifiOff className="w-3 h-3 mr-1" />Not Mapped</span>
                          )}
                        </td>
                        <td className="px-3 py-2.5">
                          <Button
                            size="sm" variant="outline"
                            onClick={() => onTestTelemetry(asset.asset_id)}
                            disabled={testingAsset === asset.asset_id && !telemetryResult}
                            data-testid={`test-telemetry-${asset.asset_id}`}
                            className="text-xs h-7"
                          >
                            <Activity className="w-3 h-3 mr-1" />
                            {testingAsset === asset.asset_id && !telemetryResult ? 'Testing...' : 'Test Link'}
                          </Button>
                          {testingAsset === asset.asset_id && telemetryResult && (
                            <div className={`mt-1.5 text-xs px-2 py-1 rounded ${
                              telemetryResult.status === 'CONNECTED' ? 'bg-green-50 text-green-700' :
                              telemetryResult.status === 'NO_DATA' ? 'bg-amber-50 text-amber-700' :
                              'bg-red-50 text-red-700'
                            }`}>
                              {telemetryResult.message}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="flex items-center justify-between mt-2">
              <p className="text-xs text-slate-400">No assets configured for this site.</p>
              <Button size="sm" variant="outline" onClick={() => onCreateAsset(site.site_id)} data-testid={`create-asset-${site.site_id}`}>
                <Plus className="w-3 h-3 mr-1" /> Add Asset
              </Button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function DocumentsTab({ documents, onUpload, onDelete, onDownload }) {
  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h3 className="font-semibold text-slate-900">Client Documents</h3>
        <Button onClick={onUpload} className="bg-slate-900 hover:bg-slate-800" data-testid="upload-doc-button">
          <Upload className="w-4 h-4 mr-2" /> Upload Document
        </Button>
      </div>

      {documents.length === 0 ? (
        <div className="text-center py-12 bg-white border border-slate-200 rounded-sm">
          <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 text-sm">No documents uploaded yet.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {documents.map(doc => (
            <div
              key={doc.doc_id}
              className="bg-white border border-slate-200 rounded-sm p-4 flex items-start justify-between"
              data-testid={`doc-card-${doc.doc_id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center space-x-2 mb-1">
                  <span className={`px-2 py-0.5 text-xs rounded border ${DOC_TYPE_COLORS[doc.doc_type] || DOC_TYPE_COLORS.other}`}>
                    {DOC_TYPE_LABELS[doc.doc_type] || doc.doc_type}
                  </span>
                </div>
                <p className="text-sm font-medium text-slate-900 truncate">{doc.original_filename}</p>
                {doc.description && <p className="text-xs text-slate-500 mt-0.5">{doc.description}</p>}
                <p className="text-xs text-slate-400 mt-1">
                  {doc.uploaded_by} &middot; {new Date(doc.created_at).toLocaleDateString()}
                </p>
              </div>
              <div className="flex space-x-1 ml-3">
                <button
                  onClick={() => onDownload(doc.doc_id)}
                  className="p-1.5 text-slate-400 hover:text-blue-600 transition-colors"
                  data-testid={`download-doc-${doc.doc_id}`}
                >
                  <Download className="w-4 h-4" />
                </button>
                <button
                  onClick={() => onDelete(doc.doc_id)}
                  className="p-1.5 text-slate-400 hover:text-red-600 transition-colors"
                  data-testid={`delete-doc-${doc.doc_id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function BillingTab({ summary, clientId, navigate }) {
  const [terms, setTerms] = useState(null);
  const [profile, setProfile] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [savingTerms, setSavingTerms] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);

  const reload = React.useCallback(async () => {
    try {
      const [t, p, i] = await Promise.all([
        invoicingAPI.getTerms(clientId),
        invoicingAPI.getBillingProfile(clientId),
        invoicingAPI.listInvoices({ client_id: clientId, limit: 100 }),
      ]);
      setTerms(t.data); setProfile(p.data); setInvoices(i.data?.rows || []);
    } catch (e) { /* silent */ }
  }, [clientId]);
  useEffect(() => { reload(); }, [reload]);

  // Pickup any pending Commercial-Terms prefill the Agreement Extract tab
  // queued before navigating here. Apply once, mark active, do NOT auto-save.
  const [prefillFlash, setPrefillFlash] = useState(null);
  useEffect(() => {
    if (!terms) return;
    const key = `voltide.agreementPrefill.${clientId}`;
    const raw = sessionStorage.getItem(key);
    if (!raw) return;
    try {
      const patch = JSON.parse(raw);
      if (patch && Object.keys(patch).length) {
        setTerms(prev => ({ ...(prev || {}), ...patch, active: true }));
        setPrefillFlash(Object.keys(patch));
        toast.success(`Pre-filled ${Object.keys(patch).length} field(s) from agreement extract — review and click Save Terms`);
      }
    } catch (e) { console.error('Failed to parse agreement extract patch', e); }
    sessionStorage.removeItem(key);
    // eslint-disable-next-line
  }, [clientId, !!terms]);

  const saveTerms = async () => {
    setSavingTerms(true);
    try { await invoicingAPI.updateTerms(clientId, terms); toast.success('Commercial terms saved'); reload(); }
    catch (e) { toast.error('Save failed'); }
    setSavingTerms(false);
  };
  const saveProfile = async () => {
    setSavingProfile(true);
    try { await invoicingAPI.updateBillingProfile(clientId, profile); toast.success('Billing profile saved'); reload(); }
    catch (e) { toast.error('Save failed'); }
    setSavingProfile(false);
  };

  const createMonthly = async () => {
    const today = new Date();
    try {
      await invoicingAPI.createMonthly({ client_id: clientId, year: today.getFullYear(), month: today.getMonth() + 1 });
      toast.success('Monthly invoice (draft) created');
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Create failed'); }
  };

  if (!terms || !profile) return <LoadingSpinner />;

  const setT = (k, v) => setTerms({ ...terms, [k]: v });
  const setBA = (k, v) => setProfile({ ...profile, billing_address: { ...(profile.billing_address || {}), [k]: v } });
  const setG = (k, v) => setProfile({ ...profile, gst_details: { ...(profile.gst_details || {}), [k]: v } });
  const setP = (k, v) => setProfile({ ...profile, [k]: v });

  return (
    <div className="space-y-6" data-testid="client-billing-tab">
      {/* Commercial Terms */}
      <div className="bg-white border border-slate-200 rounded-sm p-5">
        {prefillFlash && prefillFlash.length > 0 && (
          <div className="-mx-5 -mt-5 mb-4 px-5 py-2.5 bg-amber-50 border-b border-amber-200 flex items-center gap-2 text-xs">
            <Sparkles className="w-3.5 h-3.5 text-amber-600 flex-shrink-0" />
            <span className="text-amber-900">
              <b>Pre-filled from Agreement Extract</b> ({prefillFlash.length} field{prefillFlash.length !== 1 ? 's' : ''}: {prefillFlash.join(', ')}). Review the values and click <b>Save Terms</b> below — nothing has been persisted yet.
            </span>
            <button onClick={() => setPrefillFlash(null)} className="ml-auto text-amber-700 hover:text-amber-900">×</button>
          </div>
        )}
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-slate-900">Commercial Terms <span className="text-[10px] text-slate-400 ml-1 font-normal">SOURCE OF TRUTH</span></h3>
          <label className="flex items-center gap-2 text-xs">
            <input type="checkbox" checked={!!terms.active} onChange={e => setT('active', e.target.checked)} data-testid="terms-active" />
            Active for monthly auto-billing
          </label>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <div><Label className="text-xs">Effective From</Label><Input type="date" value={terms.effective_from || ''} onChange={e => setT('effective_from', e.target.value)} data-testid="terms-effective-from" className="h-8 text-xs" /></div>
          <div><Label className="text-xs">Monthly Fixed (ex-GST) ₹</Label><Input type="number" value={terms.monthly_fixed_amount_ex_gst || 0} onChange={e => setT('monthly_fixed_amount_ex_gst', Number(e.target.value))} data-testid="terms-monthly-amount" className="h-8 text-xs" /></div>
          <div><Label className="text-xs">GST Rate (decimal)</Label><Input type="number" step="0.01" value={terms.gst_rate || 0.18} onChange={e => setT('gst_rate', Number(e.target.value))} data-testid="terms-gst" className="h-8 text-xs" /></div>
          <div><Label className="text-xs">SAC Code</Label><Input value={terms.sac_code || ''} onChange={e => setT('sac_code', e.target.value)} data-testid="terms-sac" className="h-8 text-xs" /></div>
          <div><Label className="text-xs">Invoice Day of Month</Label><Input type="number" min={1} max={28} value={terms.invoice_day_of_month || 1} onChange={e => setT('invoice_day_of_month', Number(e.target.value))} className="h-8 text-xs" /></div>
          <div><Label className="text-xs">Due in Days</Label><Input type="number" value={terms.due_in_days || 0} onChange={e => setT('due_in_days', Number(e.target.value))} className="h-8 text-xs" /></div>
          <div><Label className="text-xs">Place of Supply (state)</Label><Input value={terms.place_of_supply_state || ''} onChange={e => setT('place_of_supply_state', e.target.value)} className="h-8 text-xs" /></div>
          <div><Label className="text-xs">Place of Supply (state code)</Label><Input value={terms.place_of_supply_state_code || ''} onChange={e => setT('place_of_supply_state_code', e.target.value)} className="h-8 text-xs" /></div>
          <div><Label className="text-xs">Notes</Label><Input value={terms.notes || ''} onChange={e => setT('notes', e.target.value)} className="h-8 text-xs" /></div>
        </div>
        <div className="mt-3 flex items-center justify-between">
          <span className="text-[10px] text-slate-400">Updated: {terms.updated_at ? new Date(terms.updated_at).toLocaleString() : '—'}</span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={createMonthly} disabled={!terms.active || !terms.monthly_fixed_amount_ex_gst} data-testid="create-monthly-btn"><FileText className="w-3.5 h-3.5 mr-1" /> Create This Month's Invoice</Button>
            <Button size="sm" onClick={saveTerms} disabled={savingTerms} data-testid="save-terms-btn">{savingTerms ? 'Saving…' : 'Save Terms'}</Button>
          </div>
        </div>
      </div>

      {/* Billing Profile */}
      <div className="bg-white border border-slate-200 rounded-sm p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-slate-900">Billing Profile <span className="text-[10px] text-slate-400 ml-1 font-normal">USED ON INVOICES</span></h3>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <div><Label className="text-xs">Legal Name (Billed To)</Label><Input value={profile.legal_name || ''} onChange={e => setP('legal_name', e.target.value)} data-testid="profile-legal-name" className="h-8 text-xs" /></div>
          <div><Label className="text-xs">Billing Address Line 1</Label><Input value={profile.billing_address?.line1 || ''} onChange={e => setBA('line1', e.target.value)} className="h-8 text-xs" /></div>
          <div><Label className="text-xs">Billing City</Label><Input value={profile.billing_address?.city || ''} onChange={e => setBA('city', e.target.value)} className="h-8 text-xs" /></div>
          <div><Label className="text-xs">State</Label><Input value={profile.billing_address?.state || ''} onChange={e => setBA('state', e.target.value)} className="h-8 text-xs" /></div>
          <div><Label className="text-xs">State Code (2 digit)</Label><Input value={profile.billing_address?.state_code || ''} onChange={e => setBA('state_code', e.target.value)} data-testid="profile-state-code" className="h-8 text-xs" /></div>
          <div><Label className="text-xs">Pincode</Label><Input value={profile.billing_address?.pincode || ''} onChange={e => setBA('pincode', e.target.value)} className="h-8 text-xs" /></div>
          <div><Label className="text-xs">GSTIN</Label><Input value={profile.gst_details?.gstin || ''} onChange={e => setG('gstin', e.target.value)} data-testid="profile-gstin" className="h-8 text-xs" /></div>
          <div><Label className="text-xs">PAN</Label><Input value={profile.gst_details?.pan || ''} onChange={e => setG('pan', e.target.value)} className="h-8 text-xs" /></div>
          <div><Label className="text-xs">Legal Name as per GST</Label><Input value={profile.gst_details?.legal_name_as_per_gst || ''} onChange={e => setG('legal_name_as_per_gst', e.target.value)} className="h-8 text-xs" /></div>
        </div>
        <div className="mt-3 flex justify-end">
          <Button size="sm" onClick={saveProfile} disabled={savingProfile} data-testid="save-profile-btn">{savingProfile ? 'Saving…' : 'Save Profile'}</Button>
        </div>
      </div>

      {/* Invoices for this client */}
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-semibold text-slate-900 text-sm">Invoices ({invoices.length})</h3>
          <Button size="sm" variant="ghost" onClick={() => navigate('/billing')} className="h-7 text-xs">Open in Billing →</Button>
        </div>
        {invoices.length === 0 ? (
          <div className="p-8 text-center text-xs text-slate-400">No invoices yet for this client.</div>
        ) : (
          <table className="w-full text-xs">
            <thead><tr className="bg-slate-50 text-slate-500">
              <th className="px-3 py-2 text-left font-medium">Number</th>
              <th className="px-3 py-2 text-left font-medium">Type</th>
              <th className="px-3 py-2 text-left font-medium">Period / Issue</th>
              <th className="px-3 py-2 text-right font-medium">Total</th>
              <th className="px-3 py-2 text-left font-medium">Status</th>
            </tr></thead>
            <tbody className="divide-y divide-slate-50">
              {invoices.map(inv => (
                <tr key={inv.invoice_id} className="hover:bg-slate-50/50">
                  <td className="px-3 py-2 font-mono">{inv.invoice_number}</td>
                  <td className="px-3 py-2">{inv.invoice_type}</td>
                  <td className="px-3 py-2 text-[11px]">{inv.invoice_period?.from_date || inv.issue_date}</td>
                  <td className="px-3 py-2 text-right font-mono">₹{Number(inv.total_inr || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                  <td className="px-3 py-2"><span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-700">{inv.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}


// ─── AgreementExtractTab ────────────────────────────────────────────
// Reads the latest agreement extract for this client, lets the user re-run
// extraction against an uploaded `agreement` document, and ships the
// deterministic prefill patch over to the Billing tab via sessionStorage.
function AgreementExtractTab({ clientId, documents, onSwitchToBilling }) {
  const [extract, setExtract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [showAdditional, setShowAdditional] = useState(false);
  const [prefill, setPrefill] = useState({});

  const reload = React.useCallback(async () => {
    setLoading(true);
    try {
      const r = await invoicingAPI.getAgreementExtract(clientId);
      setExtract(r.data?.extracted ? r.data : null);
      if (r.data?.extracted) {
        const p = await invoicingAPI.getAgreementPrefill(clientId);
        setPrefill(p.data?.patch || {});
      }
    } catch (e) { /* silent */ }
    setLoading(false);
  }, [clientId]);
  useEffect(() => { reload(); }, [reload]);

  const agreements = (documents || []).filter(d => d.doc_type === 'agreement' && !d.is_deleted);

  const runExtraction = async (doc) => {
    setRunning(true);
    toast.info('Running AI extraction… this can take 20–40 seconds');
    try {
      await invoicingAPI.runAgreementExtractFromDoc(clientId, doc.doc_id);
      toast.success('Extraction complete');
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Extraction failed');
    }
    setRunning(false);
  };

  const copyToCommercialTerms = () => {
    if (!Object.keys(prefill || {}).length) {
      return toast.error('No mappable fields to copy');
    }
    sessionStorage.setItem(`voltide.agreementPrefill.${clientId}`, JSON.stringify(prefill));
    toast.success(`Queued ${Object.keys(prefill).length} field(s) for Commercial Terms — opening Billing tab`);
    onSwitchToBilling && onSwitchToBilling();
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-5" data-testid="agreement-extract-tab">
      {/* Disclaimer banner */}
      <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 flex gap-2 text-xs text-amber-900" data-testid="agreement-extract-disclaimer">
        <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5 text-amber-700" />
        <div>
          <b>Reference-only.</b> AI extracts are best-effort and may be wrong. Invoices are generated from <b>Commercial Terms</b> on the Billing tab — that's the source of truth. Use <b>Copy into Commercial Terms</b> below to pre-fill the form, then review and Save manually.
        </div>
      </div>

      {/* Source agreement actions */}
      <div className="bg-white border border-slate-200 rounded-sm p-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="font-semibold text-slate-900 text-sm flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-amber-500" /> AI Agreement Extraction
            </h3>
            <p className="text-[11px] text-slate-500 mt-0.5">
              Extracts Tier 1 commercial terms + Tier 2 reference clauses from a signed agreement PDF using Claude Sonnet.
            </p>
          </div>
          {extract?.extracted_at && (
            <span className="text-[10px] text-slate-400">
              Last run: {new Date(extract.extracted_at).toLocaleString()} · {extract.extraction_model}
            </span>
          )}
        </div>
        {agreements.length === 0 ? (
          <div className="text-xs text-slate-500 bg-slate-50 rounded-sm p-3" data-testid="agreement-empty">
            No agreement document uploaded yet. Go to <b>Documents</b> tab → Upload a PDF with type <b>"agreement"</b>, then come back here to run extraction.
          </div>
        ) : (
          <div className="space-y-1.5">
            {agreements.map(d => (
              <div key={d.doc_id} className="flex items-center gap-3 text-xs px-3 py-2 border border-slate-200 rounded-sm">
                <FileText className="w-3.5 h-3.5 text-slate-500" />
                <span className="flex-1 truncate font-mono">{d.original_filename}</span>
                <span className="text-[10px] text-slate-400">{(d.size / 1024).toFixed(1)} KB</span>
                <Button size="sm" variant="outline" disabled={running} onClick={() => runExtraction(d)} data-testid={`extract-run-${d.doc_id}`} className="h-7">
                  {running ? <RefreshCw className="w-3.5 h-3.5 animate-spin mr-1" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
                  {extract && extract.agreement_file_id === d.doc_id ? 'Re-run extraction' : 'Run extraction'}
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Extract results */}
      {extract && (
        <>
          {/* Summary */}
          <div className="bg-white border border-slate-200 rounded-sm p-4" data-testid="agreement-summary">
            <h4 className="text-xs font-semibold text-slate-900 mb-1.5">Agreement summary</h4>
            <p className="text-sm text-slate-700 leading-relaxed">{extract.extracted_summary || '—'}</p>
          </div>

          {/* Tier 1 — primary commercial terms */}
          <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
            <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
              <h4 className="text-xs font-semibold text-slate-900">Key commercial terms (Tier 1)</h4>
              <span className="text-[10px] text-slate-500">{extract.tier_1?.length || 0} field(s)</span>
            </div>
            <ExtractedFieldTable rows={extract.tier_1 || []} />
            {Object.keys(prefill || {}).length > 0 && (
              <div className="px-4 py-2.5 bg-emerald-50/40 border-t border-emerald-200 flex items-center justify-between gap-3">
                <div className="text-[11px] text-emerald-900">
                  <b>{Object.keys(prefill).length}</b> field(s) can be auto-mapped into Commercial Terms:
                  <span className="ml-1 font-mono">{Object.keys(prefill).join(', ')}</span>
                </div>
                <Button size="sm" onClick={copyToCommercialTerms} data-testid="copy-to-terms-btn">
                  <Copy className="w-3.5 h-3.5 mr-1" /> Copy into Commercial Terms
                </Button>
              </div>
            )}
          </div>

          {/* Tier 2 — additional reference clauses (collapsed by default) */}
          <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
            <button
              onClick={() => setShowAdditional(s => !s)}
              data-testid="tier2-toggle"
              className="w-full px-4 py-2.5 bg-slate-50 border-b border-slate-200 flex items-center justify-between hover:bg-slate-100 transition-colors"
            >
              <h4 className="text-xs font-semibold text-slate-900">Additional terms (Tier 2 — reference)</h4>
              <ChevronDown className={`w-3.5 h-3.5 text-slate-500 transition-transform ${showAdditional ? 'rotate-180' : ''}`} />
            </button>
            {showAdditional && <ExtractedFieldTable rows={extract.tier_2 || []} />}
          </div>
        </>
      )}
    </div>
  );
}

function ExtractedFieldTable({ rows }) {
  if (!rows.length) return <div className="p-4 text-xs text-slate-400">No fields extracted.</div>;
  return (
    <table className="w-full text-xs" data-testid="extracted-fields-table">
      <thead>
        <tr className="text-slate-500 bg-slate-50/40 border-b border-slate-100">
          <th className="px-4 py-2 text-left font-medium w-1/4">Label</th>
          <th className="px-4 py-2 text-left font-medium">Value</th>
          <th className="px-4 py-2 text-left font-medium w-20">Conf.</th>
          <th className="px-4 py-2 text-left font-medium w-14">Page</th>
          <th className="px-4 py-2 text-left font-medium w-2/5">Snippet</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-50">
        {rows.map((r, i) => {
          const conf = r.confidence ?? 0;
          const confColor = conf >= 0.8 ? 'bg-emerald-100 text-emerald-800' : conf >= 0.5 ? 'bg-amber-100 text-amber-800' : 'bg-slate-100 text-slate-600';
          const isMissing = !r.value || r.value === 'Not specified';
          return (
            <tr key={r.label || i} className="hover:bg-slate-50/40">
              <td className="px-4 py-2 text-slate-700">{r.label}</td>
              <td className={`px-4 py-2 ${isMissing ? 'text-slate-400 italic' : 'text-slate-900 font-medium'}`}>{r.value || '—'}</td>
              <td className="px-4 py-2"><span className={`text-[10px] px-1.5 py-0.5 rounded ${confColor}`}>{(conf * 100).toFixed(0)}%</span></td>
              <td className="px-4 py-2 text-slate-500 font-mono text-[11px]">{r.source_page ? `p${r.source_page}` : '—'}</td>
              <td className="px-4 py-2 text-[10.5px] text-slate-500 italic">{r.source_snippet ? `"${r.source_snippet}"` : '—'}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
